import { supabase } from "@/integrations/supabase/client";

/**
 * Réglages CSS de la barre de progression du lecteur (feed et overlays).
 * Valeurs métier lues dans `app_settings`, jamais codées en dur dans l'UI.
 */
export interface PlayerProgressSettings {
  /** Hauteur du couloir de tap, en rem. */
  laneHeightRem: number;
  /** Épaisseur visible de la piste, en px. */
  trackHeightPx: number;
  /** Taille du pouce, en px (0 = caché). */
  thumbSizePx: number;
  /** Opacité du pouce, entre 0 et 1. */
  thumbOpacity: number;
}

export const PLAYER_PROGRESS_SETTING_KEY = "video_player_progress";

/** Défaut : fine ligne type TikTok, sans pouce visible. */
export const DEFAULT_PLAYER_PROGRESS: PlayerProgressSettings = {
  laneHeightRem: 0.75,
  trackHeightPx: 1,
  thumbSizePx: 0,
  thumbOpacity: 0,
};

/** Bornes de validation, partagées entre l'administration et le chargement. */
export const PLAYER_PROGRESS_BOUNDS = {
  laneHeightRem: { min: 0.25, max: 3 },
  trackHeightPx: { min: 1, max: 16 },
  thumbSizePx: { min: 0, max: 24 },
  thumbOpacity: { min: 0, max: 1 },
} as const;

let current: PlayerProgressSettings = { ...DEFAULT_PLAYER_PROGRESS };

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const parsed = typeof value === "string" ? Number(value) : value;
  if (typeof parsed !== "number" || !Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

/** Normalise une valeur inconnue (BDD, saisie admin) vers des réglages valides. */
export function parsePlayerProgress(value: unknown): PlayerProgressSettings {
  const raw = (value ?? {}) as Record<string, unknown>;
  const b = PLAYER_PROGRESS_BOUNDS;
  return {
    laneHeightRem: clampNumber(
      raw.lane_height_rem ?? raw.laneHeightRem,
      b.laneHeightRem.min,
      b.laneHeightRem.max,
      DEFAULT_PLAYER_PROGRESS.laneHeightRem,
    ),
    trackHeightPx: clampNumber(
      raw.track_height_px ?? raw.trackHeightPx,
      b.trackHeightPx.min,
      b.trackHeightPx.max,
      DEFAULT_PLAYER_PROGRESS.trackHeightPx,
    ),
    thumbSizePx: clampNumber(
      raw.thumb_size_px ?? raw.thumbSizePx,
      b.thumbSizePx.min,
      b.thumbSizePx.max,
      DEFAULT_PLAYER_PROGRESS.thumbSizePx,
    ),
    thumbOpacity: clampNumber(
      raw.thumb_opacity ?? raw.thumbOpacity,
      b.thumbOpacity.min,
      b.thumbOpacity.max,
      DEFAULT_PLAYER_PROGRESS.thumbOpacity,
    ),
  };
}

/** Format stocké en base (snake_case), utilisé par l'écran d'administration. */
export function toPlayerProgressPayload(settings: PlayerProgressSettings) {
  return {
    lane_height_rem: settings.laneHeightRem,
    track_height_px: settings.trackHeightPx,
    thumb_size_px: settings.thumbSizePx,
    thumb_opacity: settings.thumbOpacity,
  };
}

export function getPlayerProgress(): PlayerProgressSettings {
  return current;
}

export function setPlayerProgress(settings: PlayerProgressSettings): void {
  current = parsePlayerProgress(toPlayerProgressPayload(settings));
}

/**
 * Charge le réglage depuis la base. Best-effort : en cas d'échec on garde
 * les défauts, l'affichage du lecteur ne doit jamais dépendre de cet appel.
 */
export async function loadPlayerProgress(): Promise<PlayerProgressSettings> {
  try {
    const { data } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", PLAYER_PROGRESS_SETTING_KEY)
      .maybeSingle();

    current = parsePlayerProgress(data?.value);
  } catch {
    current = { ...DEFAULT_PLAYER_PROGRESS };
  }
  return current;
}

/** Variables CSS à poser sur le conteneur de la barre de progression. */
export function playerProgressCssVars(
  settings: PlayerProgressSettings = current,
): React.CSSProperties {
  return {
    ["--player-progress-track-height" as string]: `${settings.trackHeightPx}px`,
    ["--player-progress-thumb-size" as string]: `${settings.thumbSizePx}px`,
    ["--player-progress-thumb-opacity" as string]: `${settings.thumbOpacity}`,
  } as React.CSSProperties;
}
